import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, NgxsSimpleChange, Store } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { PrfActions } from '../actions/prf.actions';
import { Prf } from '../models/prf.model';
import { PrfLineItem } from '../models/prf-line-item.model';
import { PrfPhoto } from '../models/prf-photo.model';
import { PrfService } from 'src/app/services/prf.service';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { ConnectionStatusEnum, NetworkService } from 'src/app/services/network.service';
import { OfflineService } from 'src/app/services/offline.service';
import { DateTime } from 'luxon';
import { StorageService } from 'src/app/services/storage.service';
import { stateContentChanged } from './state-content-changed'

export class PrfStateModel {
    is_refreshing: boolean
    pending_count: number
    last_update: string
    prfs: Prf[]
}

@State<PrfStateModel>({
    name: 'prf',
    defaults: {
        is_refreshing: true,
        pending_count: 0,
        last_update: '',
        prfs: []
    }
})
@Injectable()
export class PrfState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private prfService: PrfService,        
        private networkService: NetworkService,
        private offline: OfflineService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<PrfStateModel>) {   
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {
                        await this.reloadStateFromStorage()                        
                        this.store.dispatch(new PrfActions.IncrementPendingCount())
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {               
        if (this.storageService.is_initialized && stateContentChanged(change)) {
            const state_model: PrfStateModel = change.currentValue  
            this.storageService.set('prfs', JSON.stringify(state_model))    
        }  
        if (this.networkService.currentStatus === ConnectionStatusEnum.Online) {     
            if (change.previousValue && change.previousValue.pending_count < change.currentValue.pending_count) {    
                const state_model: PrfStateModel = change.currentValue                     
                await this.offline.processPendingPrfs(state_model.prfs)                        
            }
        }       
    }

    async reloadStateFromStorage() {
        const json: string = await this.storageService.get('prfs');
        if (json) {
            const state_model: PrfStateModel = JSON.parse(json)
            if (state_model) {
                this.store.dispatch(new PrfActions.Init(state_model));
                return
            }
        }
        this.store.dispatch(new PrfActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        const objs = await this.prfService.getMine()    
        if (objs != null) {
          // The service has already run buildMany over the response, so the
          // rows arriving here are built models. Building them a second time
          // allocated a duplicate object for every row and, where buildOne
          // rebuilds children, re-walked those too.
          this.store.dispatch(new PrfActions.Merge(objs))
          return true
        }
        return false
    }

    @Selector()
    static getLastUpdate(state: PrfStateModel) {
        return state.last_update
    }

    @Selector()
    static getIsRefreshing(state: PrfStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getState(state: PrfStateModel) {
        return state
    }

    @Selector()
    static getAll(state: PrfStateModel) {
        return state.prfs
    }

    @Selector()
    static getOne(state: PrfStateModel) {
        return (id: number) => {
            return state.prfs.filter(s => s.id === id)
        }
    }

    @Action(PrfActions.Refresh)
    async refresh({patchState}: StateContext<PrfStateModel>, {  }:PrfActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(PrfActions.IncrementPendingCount)
    incrementPendingCount({patchState, getState}: StateContext<PrfStateModel>, {  }:PrfActions.IncrementPendingCount) {     
        const state = getState()
        patchState({
            pending_count: state.pending_count + 1
        })
    }
    
    @Action(PrfActions.Init)
    init({setState, getState}: StateContext<PrfStateModel>, { payload }:PrfActions.Init) {             
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new PrfActions.Refresh())
    }

    @Action(PrfActions.Set)
    set({setState, getState}: StateContext<PrfStateModel>, { payload }:PrfActions.Set) {  
        const state = getState()        
        setState({   
            is_refreshing: state.is_refreshing,  
            pending_count: 0,
            last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a"),         
            prfs: payload
        })
    }

    @Action(PrfActions.Merge)
    merge({getState, setState}: StateContext<PrfStateModel>, { payload }:PrfActions.Merge) {   
        // Server data replaces the app's, except for PRFs with a save or
        // delete still to send: those keep the local copy, whether or not the
        // server has them yet. An PRF matches by uuid, or by id once it has one.
        const state = getState()
        const local = state.prfs
        const isPending = (m: Prf) => m.is_pending_save || m.is_pending_delete
        const newPrfs: Prf[] = []

        for (const server of payload) {
            const mine = local.find(m => samePrf(m, server))
            if (mine && isPending(mine)) continue
            newPrfs.push(mine ? keepLocalOnly(server, mine) : server)
        }
        for (const mine of local) {
            if (isPending(mine)) newPrfs.push(mine)
        }

        setState({
            is_refreshing: false,
            pending_count: 0,
            last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a"),
            prfs: newPrfs
        })
    }

    @Action(PrfActions.AddOne)
    addOne({getState, patchState}: StateContext<PrfStateModel>, { payload }:PrfActions.AddOne) {
        const state = getState()        
        patchState({
            prfs: [...state.prfs, payload]
        })
    }

    // By uuid: an PRF that hasn't reached the server yet has id 0, and so
    // does every other one like it.
    @Action(PrfActions.UpdateOne)
    updateOne({setState}: StateContext<PrfStateModel>, { payload }:PrfActions.UpdateOne) {
        setState(
            patch<PrfStateModel>
                ({
                    prfs: updateItem((item: Prf) => item.uuid === payload.uuid, 
                        patch<Prf>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(PrfActions.DeleteOne)
    deleteOne({setState}: StateContext<PrfStateModel>, { payload }:PrfActions.DeleteOne) {
        setState(
            patch<PrfStateModel>
                ({
                    prfs: updateItem((item: Prf) => item.uuid === payload.uuid, 
                        patch<Prf>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(PrfActions.ApiDeletedOne)
    apiDeletedOne({ setState }: StateContext<PrfStateModel>, { payload }:PrfActions.ApiDeletedOne) {         
        setState(
            patch<PrfStateModel>
                ({
                    prfs: removeItem((item: Prf) => item.uuid === payload.uuid),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(PrfActions.ApiSavedOne)
    apiSavedOne({ getState, setState }: StateContext<PrfStateModel>, { payload }:PrfActions.ApiSavedOne) {
        const item = getState().prfs.find(m => m.uuid === payload.uuid)
        if (!item) return
        const server = Prf.buildOne(payload.server)
        let updated: Prf

        if (item.local_rev !== payload.local_rev) {
            // Saved again while this one was on its way: keep those edits and
            // take only the ids, so the next send is a PUT of the same lines.
            updated = {
                ...item,
                id: server.id,
                prf_line_items: (item.prf_line_items || []).map(l => {
                    const s = findLine(server.prf_line_items, l)
                    return s ? { ...l, id: s.id, uuid: s.uuid || l.uuid } : l
                }),
            } as Prf
        } else {
            // The server's copy, which is the save itself or, if parts had
            // already taken the PRF, theirs. Photos waiting to upload stay on
            // their lines; photos removed here stay removed.
            const deletes = item.pending_photo_deletes || []
            const lines = server.prf_line_items.map(s => {
                const mine = findLine(item.prf_line_items, s)
                const minePhotos = mine?.photos || []
                const uploaded = (s.photos || []).filter(p => !deletes.includes(p.uuid))
                const waiting = minePhotos.filter(p => !p.uploaded && !uploaded.some(u => u.uuid === p.uuid))
                return { ...s, photos: [...uploaded, ...waiting] }
            })
            updated = {
                ...server,
                uuid: item.uuid,
                is_pending_save: true,
                is_pending_delete: item.is_pending_delete,
                pending_photo_deletes: deletes,
                local_rev: item.local_rev,
                prf_line_items: lines,
            } as Prf
        }

        setState(patch<PrfStateModel>({
            prfs: updateItem((m: Prf) => m.uuid === payload.uuid, updated),
        }))
    }

    @Action(PrfActions.ApiPhotoUploaded)
    apiPhotoUploaded({ setState }: StateContext<PrfStateModel>, { payload }:PrfActions.ApiPhotoUploaded) {
        const sent = payload.photo
        setState(patch<PrfStateModel>({
            prfs: updateItem((m: Prf) => m.uuid === payload.prf_uuid, (m: Prf) => ({
                ...m,
                prf_line_items: (m.prf_line_items || []).map(l => ({
                    ...l,
                    photos: (l.photos || []).map(p => p.uuid === sent.uuid
                        ? PrfPhoto.buildOne({ ...p, ...sent, base64: '', uploaded: true })
                        : p),
                })),
            }) as Prf),
        }))
    }

    @Action(PrfActions.ApiPhotoDropped)
    apiPhotoDropped({ setState }: StateContext<PrfStateModel>, { payload }:PrfActions.ApiPhotoDropped) {
        setState(patch<PrfStateModel>({
            prfs: updateItem((m: Prf) => m.uuid === payload.prf_uuid, (m: Prf) => ({
                ...m,
                prf_line_items: (m.prf_line_items || []).map(l => ({
                    ...l,
                    photos: (l.photos || []).filter(p => p.uuid !== payload.photo_uuid),
                })),
            }) as Prf),
        }))
    }

    @Action(PrfActions.ApiPhotoDeleted)
    apiPhotoDeleted({ setState }: StateContext<PrfStateModel>, { payload }:PrfActions.ApiPhotoDeleted) {
        setState(patch<PrfStateModel>({
            prfs: updateItem((m: Prf) => m.uuid === payload.prf_uuid, (m: Prf) => ({
                ...m,
                pending_photo_deletes: (m.pending_photo_deletes || []).filter(u => u !== payload.photo_uuid),
            }) as Prf),
        }))
    }

    @Action(PrfActions.ApiSyncedOne)
    apiSyncedOne({ getState, setState }: StateContext<PrfStateModel>, { payload }:PrfActions.ApiSyncedOne) {
        const item = getState().prfs.find(m => m.uuid === payload.uuid)
        if (!item || item.local_rev !== payload.local_rev) return
        const waiting = (item.prf_line_items || []).some(l => (l.photos || []).some(p => !p.uploaded))
        if (waiting || (item.pending_photo_deletes || []).length) return
        setState(patch<PrfStateModel>({
            prfs: updateItem((m: Prf) => m.uuid === payload.uuid, patch<Prf>({ is_pending_save: false })),
            last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a"),
        }))
    }
}

function samePrf(a: Prf, b: Prf): boolean {
    return (!!a.uuid && a.uuid === b.uuid) || (a.id > 0 && a.id === b.id)
}

function findLine(lines: PrfLineItem[] | undefined, target: PrfLineItem): PrfLineItem | undefined {
    if (!lines) return undefined
    return lines.find(l => !!l.uuid && l.uuid === target.uuid)
        ?? (target.id > 0 ? lines.find(l => l.id === target.id) : undefined)
}

// What only this device knows about an PRF, kept across a refresh: its uuid
// (older PRFs have none on the server, so buildOne makes one up each time).
function keepLocalOnly(server: Prf, mine: Prf): Prf {
    return {
        ...server,
        uuid: mine.uuid,
    } as Prf
}
